import { useMemo } from "react";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { buildFallbackPagination } from "@/lib/utils/pagination";

import type {
  CreateProposalInput,
  ProposalMetricsSummary,
  ProposalQueryFilters,
  ProposalRow,
  ProposalSort,
  ProposalsQueryResult,
  UpdateProposalInput,
} from "./types";

type ProposalsApiClient = ReturnType<typeof createApiClient>;

export interface ProposalsApiContext {
  api: ProposalsApiClient;
  organizationId: string | null;
  queryEnabled: boolean;
  ensureReady: () => void;
}

export function useProposalsApi(options?: {
  enabled?: boolean;
  debugKey?: string;
  requireContextReady?: boolean;
}): ProposalsApiContext {
  const { getToken } = useAuthSafe();
  const queryState = useOrganizationQueryGuard({
    enabled: options?.enabled ?? true,
    debugKey: options?.debugKey ?? "useProposalsApi",
    requireContextReady: options?.requireContextReady,
  });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => queryState.organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, queryState.organizationId],
  );

  const ensureReady = () => {
    if (!queryState.isOrganizationReady || !queryState.organizationId) {
      throw new Error(
        "Still loading your organization. Give it a second and try again.",
      );
    }
  };

  return {
    api,
    organizationId: queryState.organizationId ?? null,
    queryEnabled: queryState.queryEnabled,
    ensureReady,
  };
}

export interface FetchProposalsArgs {
  page: number;
  limit: number;
  sort: ProposalSort;
  filters: ProposalQueryFilters;
}

export async function fetchProposals(
  api: ProposalsApiClient,
  args: FetchProposalsArgs,
): Promise<ProposalsQueryResult> {
  const searchParams = new URLSearchParams({
    page: String(args.page),
    limit: String(args.limit),
    sortBy: args.sort.field,
    sortOrder: args.sort.direction,
  });

  if (args.filters.search) searchParams.set("query", args.filters.search);
  if (args.filters.projectId)
    searchParams.set("projectId", args.filters.projectId);
  if (args.filters.clientId)
    searchParams.set("clientId", args.filters.clientId);
  if (args.filters.status) searchParams.set("status", args.filters.status);
  if (args.filters.ownerId) searchParams.set("authorId", args.filters.ownerId);
  if (args.filters.startDate)
    searchParams.set("startDate", args.filters.startDate);
  if (args.filters.endDate) searchParams.set("endDate", args.filters.endDate);
  if (args.filters.estimateId)
    searchParams.set("estimateId", args.filters.estimateId);
  if (args.filters.fields) searchParams.set("fields", args.filters.fields);

  const response = await api.get<ProposalRow[]>(
    `/api/proposals?${searchParams.toString()}`,
  );
  if (isApiFailure(response)) {
    throw new Error(response.message);
  }

  const data = Array.isArray(response.data) ? response.data : [];
  const pagination =
    response.pagination ??
    buildFallbackPagination(args.page, args.limit, data.length);

  return { data, pagination };
}

export async function fetchProposalMetrics(
  api: ProposalsApiClient,
  filters: ProposalQueryFilters,
): Promise<ProposalMetricsSummary> {
  const searchParams = new URLSearchParams();
  if (filters.status) searchParams.set("status", filters.status);
  if (filters.projectId) searchParams.set("projectId", filters.projectId);
  if (filters.estimateId) searchParams.set("estimateId", filters.estimateId);
  if (filters.ownerId) searchParams.set("authorId", filters.ownerId);
  if (filters.startDate) searchParams.set("startDate", filters.startDate);
  if (filters.endDate) searchParams.set("endDate", filters.endDate);
  if (filters.search) searchParams.set("query", filters.search);

  const url = `/api/proposals/metrics${
    searchParams.toString() ? `?${searchParams.toString()}` : ""
  }`;

  const result = await api.get<ProposalMetricsSummary>(url);
  if (isApiFailure(result)) {
    throw new Error(result.message || "Failed to fetch proposal metrics");
  }

  return (
    result.data ?? {
      total: 0,
      totalValue: 0,
      averageValue: 0,
      sentCount: 0,
      winCount: 0,
      winRate: 0,
      statusCounts: {},
    }
  );
}

export async function createProposal(
  api: ProposalsApiClient,
  payload: CreateProposalInput,
): Promise<ProposalRow> {
  const res = await api.post<ProposalRow>("/api/proposals", payload);
  if (isApiFailure(res)) {
    toastFromApiFailure("Failed to create proposal", res);
    throw new Error(res.message);
  }
  return res.data as ProposalRow;
}

export async function updateProposal(
  api: ProposalsApiClient,
  id: string,
  data: UpdateProposalInput,
): Promise<ProposalRow> {
  const res = await api.put<ProposalRow>(`/api/proposals/${id}`, data);
  if (isApiFailure(res)) {
    toastFromApiFailure("Failed to update proposal", res);
    throw new Error(res.message);
  }
  return res.data as ProposalRow;
}

export async function deleteProposal(
  api: ProposalsApiClient,
  id: string,
): Promise<void> {
  const res = await api.del<unknown>(`/api/proposals/${id}`);
  if (isApiFailure(res)) {
    toastFromApiFailure("Failed to delete proposal", res);
    throw new Error(res.message);
  }
}
